import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, TextField, IconButton, Avatar, List, ListItem,
    ListItemAvatar, ListItemText, Divider, Paper, Badge, InputAdornment, CircularProgress,
    useMediaQuery,
    useTheme,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
    Stack,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import EmojiPicker from 'emoji-picker-react';
import { apiUrl } from './LoginSignup';
import { socket } from '../socket';
import bgChat from '../assets/bg_chat.jpg';
import { enqueueSnackbar } from 'notistack';

const ACCENT = '#ff3b1f';

const playMessageSound = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(600, ctx.currentTime);
        osc.frequency.setValueAtTime(900, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.3);
    } catch (e) { }
};

const TeamInbox = () => {
    const session = JSON.parse(localStorage.getItem('userSession')) || {};
    const headers = { Authorization: session.token || '', 'Content-Type': 'application/json' };
    const theme = useTheme();
    const isTabletOrBelow = useMediaQuery(theme.breakpoints.down('md'));
    const isDark = theme.palette.mode === 'dark';

    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [search, setSearch] = useState('');
    const [activeChat, setActiveChat] = useState({ id: 'global', name: 'All Company', isGlobal: true });
    const [groupDialogOpen, setGroupDialogOpen] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
    const [mobilePane, setMobilePane] = useState('list');
    const [messages, setMessages] = useState([]);
    const [inputMsg, setInputMsg] = useState('');
    const [typingUsers, setTypingUsers] = useState({});
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editMessageText, setEditMessageText] = useState('');
    const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [groupMenuAnchor, setGroupMenuAnchor] = useState(null);
    const [manageGroupDialogOpen, setManageGroupDialogOpen] = useState(false);
    const [editingGroupMembers, setEditingGroupMembers] = useState([]);
    const [manageGroupName, setManageGroupName] = useState('');
    const [profileDialog, setProfileDialog] = useState({ open: false, user: null, isSelf: false });
    const [profilePhotoBusy, setProfilePhotoBusy] = useState(false);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const profilePhotoInputRef = useRef(null);
    const normalizedRole = (session.user_role || '').toLowerCase().trim();
    const canCreateGroup = ['admin', 'super admin', 'senior admin', 'director', 'hr', 'dev', 'srdev', 'sr dev'].includes(normalizedRole);

    // Load users
    useEffect(() => {
        fetchUsers();
        fetchGroups();
    }, []);

    const fetchUsers = async () => {
        try {
            const res = await fetch(`${apiUrl}/chat/users`, { headers });
            if (res.ok) setUsers(await res.json());
        } catch (e) { }
    };

    const fetchGroups = async () => {
        try {
            const res = await fetch(`${apiUrl}/chat/groups`, { headers });
            if (res.ok) {
                const data = await res.json();
                setGroups(data);
                data.forEach((group) => socket.emit("joinGroup", group._id));
            }
        } catch (e) { }
    };

    // Socket Connection and Global listeners
    useEffect(() => {
        if (!session.user_id) return;

        socket.connect();
        socket.emit("join", session.user_id);

        const handleReceiveMessage = (msg) => {
            // If message belongs to active chat, append it
            const belongsToGlobal = msg.is_global && activeChat.isGlobal;
            const belongsToGroup = msg.is_group && activeChat.isGroup && msg.group_id === activeChat.id;
            const belongsToDirect = !msg.is_global && !msg.is_group && !activeChat.isGlobal && !activeChat.isGroup && (msg.sender_id === activeChat.id || msg.receiver_id === activeChat.id);

            if (belongsToGlobal || belongsToGroup || belongsToDirect) {
                setMessages(prev => [...prev, msg]);
                scrollToBottom();
            }

            // If we are NOT the sender, play sound
            if (msg.sender_id !== session.user_id) {
                playMessageSound();
            }

            // Update sidebar previews
            fetchUsers();
            fetchGroups();
        };

        const handleOnlineStatus = ({ userId, isOnline }) => {
            setUsers(prev => prev.map(u => u._id === userId ? { ...u, isOnline } : u));
        };

        const handleTyping = ({ sender_id, sender_name, typing, is_global, is_group, group_id }) => {
            const chatId = is_global ? 'global' : is_group ? group_id : sender_id;
            setTypingUsers(prev => {
                const current = { ...prev };
                if (typing) current[chatId] = sender_name;
                else delete current[chatId];
                return current;
            });
        };

        const handleMessagesRead = ({ reader_id }) => {
            if (!activeChat.isGlobal && activeChat.id === reader_id) {
                setMessages(prev => prev.map(m => {
                    if (m.sender_id === session.user_id) {
                        const currentReads = m.read_by || [];
                        if (!currentReads.some(r => r.user_id === reader_id)) {
                            return { ...m, read_by: [...currentReads, { user_id: reader_id, read_at: new Date() }] };
                        }
                    }
                    return m;
                }));
            }
        };

        socket.on("receiveMessage", handleReceiveMessage);
        socket.on("user_online_status", handleOnlineStatus);
        socket.on("user_typing", handleTyping);
        socket.on("messages_read_by", handleMessagesRead);

        return () => {
            socket.off("receiveMessage", handleReceiveMessage);
            socket.off("user_online_status", handleOnlineStatus);
            socket.off("user_typing", handleTyping);
            socket.off("messages_read_by", handleMessagesRead);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeChat]);

    // Load Chat History when activeChat changes
    useEffect(() => {
        setMessages([]); // instantly clear old messages when switching chat
        const fetchHistory = async () => {
            try {
                const endpoint = activeChat.isGlobal
                    ? '/chat/global'
                    : activeChat.isGroup
                        ? `/chat/groups/${activeChat.id}/messages`
                        : `/chat/direct/${activeChat.id}`;
                const res = await fetch(`${apiUrl}${endpoint}`, { headers });
                if (res.ok) {
                    setMessages(await res.json());
                    scrollToBottom();
                    if (!activeChat.isGlobal && !activeChat.isGroup) {
                        socket.emit("messages_read", { reader_id: session.user_id, sender_id: activeChat.id });
                    }
                }
            } catch (e) { }
        };
        fetchHistory();
        // clear typing map on switch
        setTypingUsers({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeChat]);

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if (!inputMsg.trim()) return;

        socket.emit("sendMessage", {
            sender_id: session.user_id,
            sender_name: session.name,
            receiver_id: activeChat.isGlobal || activeChat.isGroup ? null : activeChat.id,
            is_global: activeChat.isGlobal,
            is_group: Boolean(activeChat.isGroup),
            group_id: activeChat.isGroup ? activeChat.id : null,
            message: inputMsg.trim()
        });

        socket.emit("typing", {
            sender_id: session.user_id,
            sender_name: session.name,
            receiver_id: activeChat.isGlobal || activeChat.isGroup ? null : activeChat.id,
            is_global: activeChat.isGlobal,
            is_group: Boolean(activeChat.isGroup),
            group_id: activeChat.isGroup ? activeChat.id : null,
            typing: false
        });

        setInputMsg('');
        setShowEmojiPicker(false);
    };

    const handleEmojiClick = (emojiObj) => {
        setInputMsg(prev => prev + emojiObj.emoji);
    };

    const handleTyping = (e) => {
        setInputMsg(e.target.value);
        socket.emit("typing", {
            sender_id: session.user_id,
            sender_name: session.name,
            receiver_id: activeChat.isGlobal || activeChat.isGroup ? null : activeChat.id,
            is_global: activeChat.isGlobal,
            is_group: Boolean(activeChat.isGroup),
            group_id: activeChat.isGroup ? activeChat.id : null,
            typing: e.target.value.length > 0
        });
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        const formData = new FormData();
        formData.append("file", file);

        try {
            const res = await fetch(`${apiUrl}/chat-upload`, {
                method: "POST",
                headers: { Authorization: session.token },
                body: formData
            });

            if (res.ok) {
                const data = await res.json();

                // Immediately emit a message with the attachment
                socket.emit("sendMessage", {
                    sender_id: session.user_id,
                    sender_name: session.name,
                    receiver_id: activeChat.isGlobal || activeChat.isGroup ? null : activeChat.id,
                    is_global: activeChat.isGlobal,
                    is_group: Boolean(activeChat.isGroup),
                    group_id: activeChat.isGroup ? activeChat.id : null,
                    message: inputMsg.trim() || "Sent an attachment",
                    attachment_url: data.attachment_url,
                    attachment_type: data.attachment_type
                });
                setInputMsg(''); // clear if they typed any text along with it
            }
        } catch (error) {
            console.error("Upload failed", error);
        } finally {
            setIsUploading(false);
            e.target.value = null; // reset input
        }
    };

    const formatTime = (ts) => {
        return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    };

    // Handle message menu open
    const handleMessageMenuOpen = (event, message) => {
        // Only allow edit/delete for own messages
        if (message.sender_id !== session.user_id) return;
        setMessageMenuAnchor(event.currentTarget);
        setSelectedMessage(message);
    };

    // Handle message menu close
    const handleMessageMenuClose = () => {
        setMessageMenuAnchor(null);
        setSelectedMessage(null);
    };

    // Start editing a message
    const handleStartEdit = () => {
        if (!selectedMessage) return;
        setEditingMessageId(selectedMessage._id);
        setEditMessageText(selectedMessage.message || '');
        handleMessageMenuClose();
    };

    // Cancel editing
    const handleCancelEdit = () => {
        setEditingMessageId(null);
        setEditMessageText('');
    };

    // Save edited message
    const handleSaveEdit = async () => {
        if (!editingMessageId || !editMessageText.trim()) return;

        try {
            const res = await fetch(`${apiUrl}/chat/messages/${editingMessageId}`, {
                method: 'PATCH',
                headers: { ...headers },
                body: JSON.stringify({ message: editMessageText.trim() })
            });

            if (res.ok) {
                const data = await res.json();
                // Update the message in the local state
                setMessages(prev => prev.map(m => 
                    m._id === editingMessageId 
                        ? { ...m, message: editMessageText.trim(), edited_at: new Date().toISOString() }
                        : m
                ));
                enqueueSnackbar('Message updated', { variant: 'success' });
            } else {
                const error = await res.json();
                enqueueSnackbar(error.message || 'Failed to update message', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error updating message:', error);
            enqueueSnackbar('Error updating message', { variant: 'error' });
        } finally {
            setEditingMessageId(null);
            setEditMessageText('');
        }
    };

    // Delete a message
    const handleDeleteMessage = async () => {
        if (!selectedMessage) return;

        if (!window.confirm('Are you sure you want to delete this message?')) {
            handleMessageMenuClose();
            return;
        }

        try {
            const res = await fetch(`${apiUrl}/chat/messages/${selectedMessage._id}`, {
                method: 'DELETE',
                headers: { ...headers }
            });

            if (res.ok) {
                // Remove the message from local state
                setMessages(prev => prev.filter(m => m._id !== selectedMessage._id));
                enqueueSnackbar('Message deleted', { variant: 'success' });
            } else {
                const error = await res.json();
                enqueueSnackbar(error.message || 'Failed to delete message', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error deleting message:', error);
            enqueueSnackbar('Error deleting message', { variant: 'error' });
        } finally {
            handleMessageMenuClose();
        }
    };

    const filteredUsers = users.filter(u => u.name.toLowerCase().includes(search.toLowerCase()));
    const filteredGroups = groups.filter(g => g.name.toLowerCase().includes(search.toLowerCase()));

    const toggleGroupMember = (userId) => {
        setSelectedGroupMembers((prev) =>
            prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
        );
    };

    const handleCreateGroup = async () => {
        if (!groupName.trim() || selectedGroupMembers.length === 0) {
            enqueueSnackbar('Enter a group name and select members', { variant: 'warning' });
            return;
        }

        try {
            const res = await fetch(`${apiUrl}/chat/groups`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ name: groupName.trim(), memberIds: selectedGroupMembers }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || 'Failed to create group');
            setGroups((prev) => [data.group, ...prev]);
            socket.emit("joinGroup", data.group._id);
            setActiveChat({ id: data.group._id, name: data.group.name, isGroup: true });
            setGroupDialogOpen(false);
            setGroupName('');
            setSelectedGroupMembers([]);
            enqueueSnackbar('Group created', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(error.message || 'Failed to create group', { variant: 'error' });
        }
    };

    const openProfileDialog = (user) => {
        if (!user) return;
        const isSelf = String(user._id || user.user_id) === String(session.user_id);
        setProfileDialog({ open: true, user, isSelf });
    };

    const closeGroupMenu = () => setGroupMenuAnchor(null);

    const openManageGroup = () => {
        if (!activeChat.isGroup) return;
        setManageGroupName(activeChat.name || '');
        setEditingGroupMembers((activeChat.members || []).map((member) => member.user_id));
        setManageGroupDialogOpen(true);
        closeGroupMenu();
    };

    const toggleEditingGroupMember = (userId) => {
        setEditingGroupMembers((prev) =>
            prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
        );
    };

    const refreshActiveGroup = (group) => {
        setGroups((prev) => prev.map((g) => g._id === group._id ? { ...g, ...group } : g));
        setActiveChat((prev) => prev.id === group._id ? { ...prev, name: group.name, members: group.members || [] } : prev);
    };

    const handleSaveGroupSettings = async () => {
        if (!activeChat.isGroup) return;
        try {
            if (manageGroupName.trim() && manageGroupName.trim() !== activeChat.name) {
                const renameRes = await fetch(`${apiUrl}/chat/groups/${activeChat.id}`, {
                    method: 'PATCH',
                    headers,
                    body: JSON.stringify({ name: manageGroupName.trim() }),
                });
                const renameData = await renameRes.json().catch(() => ({}));
                if (!renameRes.ok) throw new Error(renameData.message || 'Failed to rename group');
                refreshActiveGroup(renameData.group);
            }

            const membersRes = await fetch(`${apiUrl}/chat/groups/${activeChat.id}/members`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify({ memberIds: editingGroupMembers }),
            });
            const membersData = await membersRes.json().catch(() => ({}));
            if (!membersRes.ok) throw new Error(membersData.message || 'Failed to update members');
            refreshActiveGroup(membersData.group);
            setManageGroupDialogOpen(false);
            await fetchGroups();
            enqueueSnackbar('Group updated', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(error.message || 'Failed to update group', { variant: 'error' });
        }
    };

    const handleDeleteGroup = async () => {
        if (!activeChat.isGroup || !window.confirm('Delete this group and all group messages?')) return;
        try {
            const res = await fetch(`${apiUrl}/chat/groups/${activeChat.id}`, { method: 'DELETE', headers });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || 'Failed to delete group');
            setGroups((prev) => prev.filter((g) => g._id !== activeChat.id));
            setActiveChat({ id: 'global', name: 'All Company', isGlobal: true });
            closeGroupMenu();
            enqueueSnackbar('Group deleted', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(error.message || 'Failed to delete group', { variant: 'error' });
        }
    };

    const handleProfilePictureUpload = async (file) => {
        if (!file) return;
        setProfilePhotoBusy(true);
        const fd = new FormData();
        fd.append('profilePicture', file);
        try {
            const res = await fetch(`${apiUrl}/user/profile-picture`, {
                method: 'POST',
                headers: { Authorization: session.token || '' },
                body: fd,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || 'Failed to update photo');
            const nextSession = { ...session, profilePicture: data.user?.profilePicture || '' };
            localStorage.setItem('userSession', JSON.stringify(nextSession));
            setUsers((prev) => prev.map((u) => String(u._id) === String(session.user_id) ? { ...u, profilePicture: nextSession.profilePicture } : u));
            setProfileDialog((prev) => ({ ...prev, user: { ...(prev.user || {}), profilePicture: nextSession.profilePicture } }));
            enqueueSnackbar('Profile photo updated', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(error.message || 'Failed to update photo', { variant: 'error' });
        } finally {
            setProfilePhotoBusy(false);
            if (profilePhotoInputRef.current) profilePhotoInputRef.current.value = '';
        }
    };

    const handleProfilePictureDelete = async () => {
        setProfilePhotoBusy(true);
        try {
            const res = await fetch(`${apiUrl}/user/profile-picture`, {
                method: 'DELETE',
                headers: { Authorization: session.token || '' },
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || 'Failed to delete photo');
            const nextSession = { ...session };
            delete nextSession.profilePicture;
            localStorage.setItem('userSession', JSON.stringify(nextSession));
            setUsers((prev) => prev.map((u) => String(u._id) === String(session.user_id) ? { ...u, profilePicture: '' } : u));
            setProfileDialog((prev) => ({ ...prev, user: { ...(prev.user || {}), profilePicture: '' } }));
            enqueueSnackbar('Profile photo deleted', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(error.message || 'Failed to delete photo', { variant: 'error' });
        } finally {
            setProfilePhotoBusy(false);
        }
    };

    useEffect(() => {
        if (isTabletOrBelow) {
            setMobilePane('list');
        }
    }, [isTabletOrBelow]);

    // Determine active contact formatting
    const activeUser = !activeChat.isGlobal && !activeChat.isGroup ? users.find(u => u._id === activeChat.id) : null;

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 1560,
                mx: 'auto',
                display: 'flex',
                flexDirection: isTabletOrBelow ? 'column' : 'row',
                height: { xs: 'calc(100vh - 132px)', md: 'calc(100vh - 100px)' },
                bgcolor: 'background.paper',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: isDark ? '0 16px 34px rgba(2,6,23,0.35)' : '0 16px 36px rgba(15,23,42,0.06)',
                animation: 'fadeSlideIn 320ms ease',
            }}
        >
            <style>{`
                @keyframes bounceSlideUp {
                    0% { opacity: 0; transform: translateY(15px) scale(0.98); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}</style>

            {/* LEFT PANE = CONTACTS */}
            <Box
                sx={{
                    width: { xs: '100%', md: 320 },
                    borderRight: { xs: 'none', md: '1px solid' },
                    borderBottom: { xs: '1px solid', md: 'none' },
                    borderColor: 'divider',
                    bgcolor: isDark ? 'rgba(15,23,42,0.72)' : '#f8fafc',
                    display: isTabletOrBelow && mobilePane === 'chat' ? 'none' : 'flex',
                    flexDirection: 'column',
                    height: { xs: '100%', md: 'auto' },
                }}
            >
                <Box sx={{ p: { xs: 1.4, sm: 2 }, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Team Inbox</Typography>
                        {canCreateGroup && (
                            <IconButton size="small" onClick={() => setGroupDialogOpen(true)} sx={{ color: ACCENT }}>
                                <GroupAddIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search conversations..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                            sx: { borderRadius: '8px', bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#ffffff' }
                        }}
                    />
                </Box>

                <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
                    {/* Global Chat Item */}
                    <ListItem
                        button
                        onClick={() => {
                            setActiveChat({ id: 'global', name: 'All Company', isGlobal: true });
                            if (isTabletOrBelow) setMobilePane('chat');
                        }}
                        sx={{ bgcolor: activeChat.isGlobal ? 'rgba(232,124,42,0.08)' : 'inherit', borderLeft: activeChat.isGlobal ? `4px solid ${ACCENT}` : '4px solid transparent', mx: 1, my: 0.5, borderRadius: '8px' }}
                    >
                        <ListItemAvatar>
                            <Avatar sx={{ bgcolor: ACCENT, color: '#fff' }}>AC</Avatar>
                        </ListItemAvatar>
                        <ListItemText primary="All Company Group" secondary="Company-wide announcements" primaryTypographyProps={{ fontWeight: activeChat.isGlobal ? 700 : 500 }} />
                    </ListItem>
                    <Divider />

                    {filteredGroups.length > 0 && (
                        <>
                            {filteredGroups.map((group) => {
                                const isActive = activeChat.isGroup && activeChat.id === group._id;
                                const lastMsg = group.lastMessage ? `${group.lastMessage.sender_name}: ${group.lastMessage.message || 'Attachment'}` : `${group.members?.length || 0} members`;
                                return (
                                    <ListItem
                                        key={group._id}
                                        button
                                        onClick={() => {
                                            setActiveChat({ id: group._id, name: group.name, isGroup: true, members: group.members || [], created_by: group.created_by });
                                            if (isTabletOrBelow) setMobilePane('chat');
                                        }}
                                        sx={{ bgcolor: isActive ? 'rgba(232,124,42,0.08)' : 'inherit', borderLeft: isActive ? `4px solid ${ACCENT}` : '4px solid transparent', mx: 1, my: 0.5, borderRadius: '8px' }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar onClick={(e) => { e.stopPropagation(); setActiveChat({ id: group._id, name: group.name, isGroup: true, members: group.members || [], created_by: group.created_by }); setManageGroupName(group.name); setEditingGroupMembers((group.members || []).map((m) => m.user_id)); setManageGroupDialogOpen(true); }} sx={{ bgcolor: '#6366f1', color: '#fff', cursor: 'pointer' }}>{group.name.charAt(0)}</Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={group.name}
                                            secondary={typingUsers[group._id] ? 'typing...' : lastMsg}
                                            secondaryTypographyProps={{ noWrap: true, color: typingUsers[group._id] ? ACCENT : 'text.secondary' }}
                                            primaryTypographyProps={{ fontWeight: isActive ? 700 : 500 }}
                                        />
                                        {group.lastMessage && (
                                            <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', top: 12, right: 16 }}>
                                                {formatTime(group.lastMessage.createdAt)}
                                            </Typography>
                                        )}
                                    </ListItem>
                                );
                            })}
                            <Divider />
                        </>
                    )}

                    {/* DMs List */}
                    {filteredUsers.map(u => {
                        const isActive = activeChat.id === u._id;
                        const lastMsg = u.lastMessage ? u.lastMessage.message : 'Draft available';
                        const unread = u.lastMessage && u.lastMessage.sender_id !== session.user_id && !u.lastMessage.read_by?.some(r => r.user_id === session.user_id);

                        return (
                            <ListItem
                                key={u._id}
                                button
                                onClick={() => {
                                    setActiveChat({ id: u._id, name: u.name, isGlobal: false });
                                    if (isTabletOrBelow) setMobilePane('chat');
                                }}
                                sx={{ bgcolor: isActive ? 'rgba(232,124,42,0.08)' : 'inherit', borderLeft: isActive ? `4px solid ${ACCENT}` : '4px solid transparent', mx: 1, my: 0.5, borderRadius: '8px' }}
                            >
                                <ListItemAvatar>
                                    <Badge
                                        overlap="circular"
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        variant="dot"
                                        sx={{ '& .MuiBadge-badge': { backgroundColor: u.isOnline ? '#44b700' : '#bdbdbd', width: 10, height: 10, borderRadius: '50%', border: '2px solid white' } }}
                                    >
                                        <Avatar src={u.profilePicture || ''} onClick={(e) => { e.stopPropagation(); openProfileDialog(u); }} sx={{ bgcolor: 'primary.main', cursor: 'pointer' }}>
                                            {!u.profilePicture && u.name.charAt(0)}
                                        </Avatar>
                                    </Badge>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={u.name}
                                    secondary={typingUsers[u._id] ? 'typing...' : lastMsg}
                                    secondaryTypographyProps={{
                                        noWrap: true,
                                        fontWeight: unread ? 700 : 400,
                                        color: typingUsers[u._id] ? ACCENT : (unread ? 'text.primary' : 'text.secondary')
                                    }}
                                    primaryTypographyProps={{ fontWeight: isActive ? 700 : 500 }}
                                />
                                {u.lastMessage && (
                                    <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', top: 12, right: 16 }}>
                                        {formatTime(u.lastMessage.createdAt)}
                                    </Typography>
                                )}
                            </ListItem>
                        );
                    })}
                </List>
            </Box>

            {/* RIGHT PANE = CHAT AREA */}
            <Box
                sx={{
                    flex: 1,
                    display: isTabletOrBelow && mobilePane === 'list' ? 'none' : 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Chat Header */}
                <Box sx={{ p: { xs: 1.2, sm: 2 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {isTabletOrBelow && (
                            <IconButton size="small" onClick={() => setMobilePane('list')}>
                                <ArrowBackRoundedIcon />
                            </IconButton>
                        )}
                        <Avatar
                            src={!activeChat.isGlobal && !activeChat.isGroup && activeUser ? (activeUser.profilePicture || '') : ''}
                            onClick={() => activeChat.isGroup ? openManageGroup() : activeUser ? openProfileDialog(activeUser) : null}
                            sx={{ bgcolor: activeChat.isGlobal ? ACCENT : activeChat.isGroup ? '#6366f1' : 'primary.main', cursor: activeChat.isGlobal ? 'default' : 'pointer' }}
                        >
                            {(!activeChat.isGlobal && !activeChat.isGroup && activeUser?.profilePicture) ? null : activeChat.name.charAt(0)}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{activeChat.name}</Typography>
                            {!activeChat.isGlobal && activeUser && (
                                <Typography variant="caption" color={activeUser.isOnline ? 'success.main' : 'text.secondary'} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: activeUser.isOnline ? '#44b700' : '#bdbdbd' }} />
                                    {activeUser.isOnline ? 'Active Now' : 'Offline'}
                                </Typography>
                            )}
                            {activeChat.isGroup && (
                                <Typography variant="caption" color="text.secondary">
                                    {activeChat.members?.length || 0} members
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" onClick={(e) => activeChat.isGroup ? setGroupMenuAnchor(e.currentTarget) : activeUser ? openProfileDialog(activeUser) : null}>
                            <MoreVertIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Chat Messages Log */}
                <Box sx={{ 
                    flex: 1, 
                    p: { xs: 1.5, sm: 2.5, md: 3 }, 
                    overflowY: 'auto', 
                    bgcolor: isDark ? '#0b1220' : '#efeae2',
                    backgroundImage: isTabletOrBelow ? 'none' : `url(${bgChat})`,
                    backgroundRepeat: 'repeat',
                    backgroundBlendMode: isDark ? 'overlay' : 'normal',
                    opacity: isDark ? 0.9 : 1,
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {messages.length === 0 && (
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Paper elevation={0} sx={{ p: 1.5, px: 3, borderRadius: 8, bgcolor: isDark ? 'rgba(30,41,59,0.7)' : 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
                                <Typography variant="body2" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontWeight: 500 }}>
                                    Send a message to start the conversation
                                </Typography>
                            </Paper>
                        </Box>
                    )}
                    {messages.map((m, i) => {
                        const isMe = m.sender_id === session.user_id;
                        const showName = (activeChat.isGlobal || activeChat.isGroup) && !isMe;
                        const sender = users.find(u => u._id === m.sender_id);
                        const senderProfilePic = isMe ? session.profilePicture : sender?.profilePicture;
                        
                        return (
                            <Box 
                                key={m._id || i} 
                                sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: isMe ? 'flex-end' : 'flex-start', 
                                    mb: 2,
                                    opacity: 0,
                                    animation: `bounceSlideUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.25) forwards`,
                                }}
                            >
                                {showName && <Typography variant="caption" sx={{ ml: 1, mb: 0.5, color: 'text.secondary' }}>{m.sender_name}</Typography>}
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexDirection: isMe ? 'row-reverse' : 'row' }}>
                                    <Avatar
                                        src={senderProfilePic || ''}
                                        onClick={() => openProfileDialog(isMe ? { _id: session.user_id, name: session.name, email: session.email, profilePicture: session.profilePicture } : sender)}
                                        sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: isMe ? ACCENT : 'primary.main', cursor: 'pointer' }}
                                    >
                                        {!senderProfilePic && m.sender_name.charAt(0)}
                                    </Avatar>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 1.5,
                                                px: 2,
                                                maxWidth: { xs: '82vw', sm: 360, md: 420 },
                                                background: isMe ? 'linear-gradient(135deg, #ff512f 0%, #dd2476 100%)' : (isDark ? '#1e293b' : '#ffffff'),
                                                color: isMe ? '#fff' : (isDark ? '#f8fafc' : '#111827'),
                                                borderRadius: 3,
                                                borderTopRightRadius: isMe ? 0 : 12,
                                                borderTopLeftRadius: !isMe ? 0 : 12,
                                                border: isMe ? 'none' : (isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)'),
                                                boxShadow: isMe ? '0 4px 14px rgba(221, 36, 118, 0.3)' : (isDark ? '0 4px 12px rgba(0,0,0,0.2)' : '0 4px 12px rgba(0,0,0,0.03)')
                                            }}
                                        >
                                        {m.attachment_url && (
                                            <Box sx={{ mb: 1 }}>
                                                {m.attachment_type === 'image' ? (
                                                    <a href={m.attachment_url} target="_blank" rel="noreferrer">
                                                        <img src={m.attachment_url} alt="attachment" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }} />
                                                    </a>
                                                ) : m.attachment_type === 'video' ? (
                                                    <video src={m.attachment_url} controls style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }} />
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                                                        <InsertDriveFileIcon sx={{ color: isMe ? '#fff' : 'text.secondary' }} />
                                                        <a href={m.attachment_url} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline', wordBreak: 'break-all' }}>
                                                            View Attachment Document
                                                        </a>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                        
                                        {/* Edit Mode or Message Display */}
                                        {editingMessageId === m._id ? (
                                            <Box sx={{ minWidth: 200 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={editMessageText}
                                                    onChange={(e) => setEditMessageText(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                            e.preventDefault();
                                                            handleSaveEdit();
                                                        }
                                                        if (e.key === 'Escape') {
                                                            handleCancelEdit();
                                                        }
                                                    }}
                                                    autoFocus
                                                    sx={{
                                                        '& .MuiInputBase-root': {
                                                            bgcolor: 'rgba(255,255,255,0.9)',
                                                            borderRadius: 1,
                                                        }
                                                    }}
                                                />
                                                <Box sx={{ display: 'flex', gap: 0.5, mt: 1, justifyContent: 'flex-end' }}>
                                                    <IconButton size="small" onClick={handleSaveEdit} sx={{ color: '#10b981' }}>
                                                        <CheckIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={handleCancelEdit} sx={{ color: '#ef4444' }}>
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        ) : (
                                            m.message && m.message !== "Sent an attachment" && (
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        whiteSpace: 'pre-wrap', 
                                                        color: isMe ? '#ffffff' : 'inherit',
                                                        cursor: isMe ? 'pointer' : 'default',
                                                    }}
                                                    onClick={(e) => isMe && handleMessageMenuOpen(e, m)}
                                                >
                                                    {m.message}
                                                    {m.edited_at && (
                                                        <Typography 
                                                            component="span" 
                                                            variant="caption" 
                                                            sx={{ 
                                                                ml: 0.5, 
                                                                opacity: 0.7,
                                                                fontSize: '0.7rem'
                                                            }}
                                                        >
                                                            (edited)
                                                        </Typography>
                                                    )}
                                                </Typography>
                                            )
                                        )}
                                        
                                        {/* Edit/Delete Menu for own messages */}
                                        {isMe && editingMessageId !== m._id && (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleMessageMenuOpen(e, m)}
                                                sx={{
                                                    position: 'absolute',
                                                    top: -8,
                                                    [isMe ? 'left' : 'right']: -8,
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s',
                                                    bgcolor: isDark ? 'rgba(30,41,59,0.9)' : 'rgba(255,255,255,0.9)',
                                                    '&:hover': { opacity: 1 },
                                                    '.MuiBox-root:hover &': { opacity: 0.7 },
                                                }}
                                            >
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Paper>
                                </Box>
                                <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary', px: 1, display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                                    {formatTime(m.createdAt || new Date())}
                                    {isMe && !activeChat.isGlobal && !activeChat.isGroup && (() => {
                                        const isRead = m.read_by?.some(r => r.user_id === activeChat.id);
                                        const isDelivered = activeUser?.isOnline;
                                        if (isRead) return <DoneAllIcon sx={{ fontSize: 16, color: '#3b82f6' }} />;
                                        if (isDelivered) return <DoneAllIcon sx={{ fontSize: 16 }} />;
                                        return <DoneIcon sx={{ fontSize: 16 }} />;
                                    })()}
                                </Typography>
                            </Box>
                        );
                    })}
                    {typingUsers[activeChat.isGlobal ? 'global' : activeChat.id] && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', ml: 5 }}>
                            {typingUsers[activeChat.isGlobal ? 'global' : activeChat.id]} is typing...
                        </Typography>
                    )}
                    <div ref={messagesEndRef} />
                </Box>

                <Box sx={{ p: { xs: 1.2, sm: 2 }, borderTop: '1px solid', borderColor: 'divider', bgcolor: isDark ? '#0f172a' : '#fff', position: 'relative' }}>

                    {/* Emoji Picker Popover */}
                    {showEmojiPicker && (
                        <Box sx={{ position: 'absolute', bottom: '100%', left: 24, zIndex: 10 }}>
                            <EmojiPicker onEmojiClick={handleEmojiClick} theme={isDark ? "dark" : "light"} />
                        </Box>
                    )}

                    <form onSubmit={handleSend} style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Paper elevation={0} sx={{ flex: 1, display: 'flex', alignItems: 'center', px: 1, py: 0.5, bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'action.hover', borderRadius: '8px' }}>
                            <IconButton size="small" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                                <SentimentSatisfiedAltIcon />
                            </IconButton>

                            <input
                                type="file"
                                hidden
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                accept="image/*,video/*,.pdf,.doc,.docx,.ppt,.pptx"
                            />
                            <IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
                                {isUploading ? <CircularProgress size={20} /> : <AttachFileIcon />}
                            </IconButton>

                            <TextField
                                fullWidth
                                placeholder={`Type your reply to ${activeChat.name}...`}
                                variant="standard"
                                InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                        color: isDark ? '#f8fafc' : 'inherit',
                                        '& input::placeholder': {
                                            color: isDark ? 'rgba(248,250,252,0.6)' : 'rgba(0,0,0,0.5)',
                                            opacity: 1,
                                        },
                                    }
                                }}
                                sx={{
                                    ml: 1,
                                    py: 1,
                                    '& .MuiInput-root': {
                                        color: isDark ? '#f8fafc' : 'inherit',
                                    },
                                }}
                                value={inputMsg}
                                onChange={handleTyping}
                            />
                        </Paper>
                        <IconButton
                            type="submit"
                            disabled={!inputMsg.trim()}
                            sx={{
                                bgcolor: isDark ? '#111827' : ACCENT,
                                color: isDark ? '#ffffff' : '#fff',
                                borderRadius: '8px',
                                border: isDark ? '1px solid rgba(248,250,252,0.35)' : 'none',
                                boxShadow: isDark ? '0 6px 18px rgba(0,0,0,0.45)' : 'none',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: isDark ? '#1f2937' : '#e03118'
                                },
                                '&.Mui-disabled': {
                                    bgcolor: isDark ? '#334155' : '#e0e0e0',
                                    color: isDark ? '#f1f5f9' : '#9e9e9e',
                                    opacity: 1
                                },
                                '& svg': {
                                    color: isDark ? '#ffffff' : '#fff'
                                }
                            }}
                        >
                            <SendIcon fontSize="small" />
                        </IconButton>
                    </form>
                </Box>
            </Box>

            {/* Message Edit/Delete Menu */}
            <Menu
                anchorEl={messageMenuAnchor}
                open={Boolean(messageMenuAnchor)}
                onClose={handleMessageMenuClose}
                PaperProps={{
                    sx: {
                        minWidth: 120,
                        '& .MuiMenuItem-root': {
                            fontSize: '0.875rem',
                            gap: 1,
                        }
                    }
                }}
            >
                <MenuItem onClick={handleStartEdit}>
                    <EditIcon fontSize="small" sx={{ color: '#6366f1' }} />
                    Edit
                </MenuItem>
                <MenuItem onClick={handleDeleteMessage} sx={{ color: '#ef4444' }}>
                    <DeleteIcon fontSize="small" />
                    Delete
                </MenuItem>
            </Menu>

            <Menu anchorEl={groupMenuAnchor} open={Boolean(groupMenuAnchor)} onClose={closeGroupMenu}>
                <MenuItem onClick={openManageGroup}>View members</MenuItem>
                <MenuItem onClick={openManageGroup}>Add / remove members</MenuItem>
                <MenuItem onClick={openManageGroup}>Change group name</MenuItem>
                <MenuItem onClick={handleDeleteGroup} sx={{ color: '#ef4444' }}>Delete group</MenuItem>
            </Menu>

            <Dialog open={manageGroupDialogOpen} onClose={() => setManageGroupDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Group Details</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Group name"
                        value={manageGroupName}
                        onChange={(e) => setManageGroupName(e.target.value)}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Members</Typography>
                    <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                        {users.filter((u) => u._id !== session.user_id).map((u) => (
                            <FormControlLabel
                                key={u._id}
                                control={
                                    <Checkbox
                                        checked={editingGroupMembers.includes(u._id)}
                                        onChange={() => toggleEditingGroupMember(u._id)}
                                    />
                                }
                                label={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Avatar src={u.profilePicture || ''} sx={{ width: 24, height: 24, fontSize: 12 }}>{!u.profilePicture && u.name?.charAt(0)}</Avatar>
                                        <span>{u.name}</span>
                                    </Stack>
                                }
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setManageGroupDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveGroupSettings}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={profileDialog.open} onClose={() => setProfileDialog({ open: false, user: null, isSelf: false })} maxWidth="xs" fullWidth>
                <DialogTitle>{profileDialog.isSelf ? 'Your Profile Photo' : profileDialog.user?.name}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'grid', placeItems: 'center', py: 2 }}>
                        <Avatar src={profileDialog.user?.profilePicture || ''} sx={{ width: 160, height: 160, fontSize: 56, bgcolor: profileDialog.isSelf ? ACCENT : 'primary.main' }}>
                            {!profileDialog.user?.profilePicture && profileDialog.user?.name?.charAt(0)}
                        </Avatar>
                        {profileDialog.user?.email && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{profileDialog.user.email}</Typography>
                        )}
                    </Box>
                    {profileDialog.isSelf && (
                        <>
                            <input
                                ref={profilePhotoInputRef}
                                hidden
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleProfilePictureUpload(e.target.files?.[0])}
                            />
                            <Stack direction="row" spacing={1} justifyContent="center">
                                <Button variant="contained" disabled={profilePhotoBusy} onClick={() => profilePhotoInputRef.current?.click()}>
                                    Update Photo
                                </Button>
                                <Button color="error" disabled={profilePhotoBusy || !profileDialog.user?.profilePicture} onClick={handleProfilePictureDelete}>
                                    Delete Photo
                                </Button>
                            </Stack>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setProfileDialog({ open: false, user: null, isSelf: false })}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Create Group</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Group name"
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Members</Typography>
                    <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                        {users.filter((u) => u._id !== session.user_id).map((u) => (
                            <FormControlLabel
                                key={u._id}
                                control={
                                    <Checkbox
                                        checked={selectedGroupMembers.includes(u._id)}
                                        onChange={() => toggleGroupMember(u._id)}
                                    />
                                }
                                label={u.name}
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateGroup}>Create</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default TeamInbox;
